import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import { Avatar, Box, ClickAwayListener, Divider, Paper, Popper, Stack, Tooltip, Typography } from '@mui/material';

// terceiros
import EmojiPicker, { SkinTones } from 'emoji-picker-react';

// ativos
import { ArrowLeftOutlined, PaperClipOutlined, SendOutlined, SmileOutlined, MoreOutlined } from '@ant-design/icons';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';

// importação do projeto
import IconButton from '../../../../../components/@extended/IconButton';
import MainCard from '../../../../../components/MainCard';
import { ThemeMode } from '../../../../../config';
import { corDoAvatar, obterIniciais } from './mockChat';

// ==============================|| CHAT - THREAD DA CONVERSA ||============================== //

const rotuloStatus = { online: 'online', ausente: 'ausente', offline: 'visto por último há 2 h' };

const Conversa = ({ membro, mensagens, digitando, onEnviar, onVoltar }) => {
  const theme = useTheme();
  const modoEscuro = theme.palette.mode === ThemeMode.DARK;
  const cor = corDoAvatar(theme, membro.id);

  const [texto, setTexto] = useState('');
  const [emojiAberto, setEmojiAberto] = useState(false);
  const refEmoji = useRef(null);
  const refFim = useRef(null);

  useEffect(() => {
    refFim.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [mensagens, digitando]);

  const enviar = () => {
    if (!texto.trim()) return;
    onEnviar(texto.trim());
    setTexto('');
  };

  const aoTeclar = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  // Fundo da conversa e bolhas derivados da paleta ativa, para acompanhar o preset de cores.
  const fundoConversa = modoEscuro ? theme.palette.background.default : alpha(theme.palette.primary.main, 0.07);
  const bolhaEu = modoEscuro ? alpha(theme.palette.primary.main, 0.32) : theme.palette.primary.lighter;
  const bolhaContato = modoEscuro ? theme.palette.background.paper : theme.palette.common.white;

  const iconeSituacao = (situacao) => {
    const corLida = modoEscuro ? theme.palette.primary.lighter : theme.palette.primary.main;
    const corPadrao = alpha(theme.palette.text.secondary, 0.7);
    if (situacao === 'lida') return <DoneAllIcon sx={{ fontSize: 14, color: corLida }} />;
    if (situacao === 'entregue') return <DoneAllIcon sx={{ fontSize: 14, color: corPadrao }} />;
    return <DoneIcon sx={{ fontSize: 14, color: corPadrao }} />;
  };

  let diaAnterior = null;

  return (
    <Stack sx={{ height: '100%', flexGrow: 1, minWidth: 0, minHeight: 0 }}>
      {/* Cabeçalho do contato */}
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2, py: 1.25, bgcolor: 'background.paper' }}>
        {onVoltar && (
          <IconButton size="small" color="secondary" sx={{ color: 'text.secondary', ml: -1 }} onClick={onVoltar} aria-label="voltar para conversas">
            <ArrowLeftOutlined />
          </IconButton>
        )}
        <Avatar sx={{ width: 36, height: 36, fontSize: '0.8125rem', color: cor.main, bgcolor: cor.lighter }}>
          {obterIniciais(membro.nome)}
        </Avatar>
        <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap>
            {membro.nome}
          </Typography>
          <Typography variant="caption" noWrap sx={{ color: membro.status === 'online' ? 'success.main' : 'text.secondary' }}>
            {digitando ? 'digitando...' : `${membro.area} • ${rotuloStatus[membro.status]}`}
          </Typography>
        </Stack>
        <IconButton size="small" color="secondary" sx={{ color: 'text.secondary' }} aria-label="mais opções da conversa">
          <MoreOutlined />
        </IconButton>
      </Stack>
      <Divider />

      {/* Histórico */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', px: 2, py: 1.5, bgcolor: fundoConversa }}>
        {mensagens.map((msg) => {
          const novoDia = msg.dia !== diaAnterior;
          diaAnterior = msg.dia;
          const souEu = msg.remetente === 'eu';
          return (
            <Box key={`${msg.dia}-${msg.id}`}>
              {novoDia && (
                <Stack alignItems="center" sx={{ my: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1.25,
                      py: 0.375,
                      borderRadius: 1,
                      bgcolor: modoEscuro ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.white, 0.9),
                      color: 'text.secondary',
                      boxShadow: theme.customShadows?.z1
                    }}
                  >
                    {msg.dia}
                  </Typography>
                </Stack>
              )}
              <Stack direction="row" justifyContent={souEu ? 'flex-end' : 'flex-start'} sx={{ mb: 0.75 }}>
                <Box
                  sx={{
                    maxWidth: '78%',
                    px: 1.25,
                    py: 0.75,
                    bgcolor: souEu ? bolhaEu : bolhaContato,
                    border: souEu ? 'none' : `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                    borderRadius: 1.5,
                    borderTopRightRadius: souEu ? 2 : 12,
                    borderTopLeftRadius: souEu ? 12 : 2,
                    boxShadow: modoEscuro ? 'none' : '0 1px 1px rgba(0,0,0,0.06)'
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.primary' }}>
                    {msg.texto}
                  </Typography>
                  <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.375} sx={{ mt: 0.125 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                      {msg.hora}
                    </Typography>
                    {souEu && iconeSituacao(msg.situacao)}
                  </Stack>
                </Box>
              </Stack>
            </Box>
          );
        })}

        {digitando && (
          <Stack direction="row" justifyContent="flex-start" sx={{ mb: 0.75 }}>
            <Box
              sx={{
                px: 1.5,
                py: 1,
                bgcolor: bolhaContato,
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                borderRadius: 1.5,
                borderTopLeftRadius: 2
              }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: 'text.secondary',
                      opacity: 0.5,
                      animation: 'egrcChatDigitando 1.2s infinite ease-in-out',
                      animationDelay: `${i * 0.16}s`,
                      '@keyframes egrcChatDigitando': {
                        '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.35 },
                        '30%': { transform: 'translateY(-3px)', opacity: 0.9 }
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        )}
        <Box ref={refFim} />
      </Box>

      {/* Composer */}
      <Divider />
      <Stack direction="row" alignItems="flex-end" spacing={0.5} sx={{ px: 1, py: 1, bgcolor: 'background.paper' }}>
        <Tooltip title="Emoji">
          <IconButton
            ref={refEmoji}
            size="small"
            color="secondary"
            sx={{ color: 'text.secondary' }}
            onClick={() => setEmojiAberto((a) => !a)}
            aria-label="inserir emoji"
          >
            <SmileOutlined />
          </IconButton>
        </Tooltip>
        <Popper open={emojiAberto} anchorEl={refEmoji.current} placement="top-start" sx={{ zIndex: 2100 }}>
          <ClickAwayListener onClickAway={() => setEmojiAberto(false)}>
            <MainCard elevation={8} content={false}>
              <EmojiPicker
                onEmojiClick={(dados) => setTexto((t) => t + dados.emoji)}
                defaultSkinTone={SkinTones.NEUTRAL}
                autoFocusSearch={false}
                height={340}
                width={300}
                previewConfig={{ showPreview: false }}
              />
            </MainCard>
          </ClickAwayListener>
        </Popper>
        <Tooltip title="Anexar arquivo">
          <IconButton size="small" color="secondary" sx={{ color: 'text.secondary' }} aria-label="anexar arquivo">
            <PaperClipOutlined />
          </IconButton>
        </Tooltip>
        <Paper
          variant="outlined"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            borderRadius: 4,
            px: 1.5,
            py: 0.75,
            bgcolor: modoEscuro ? 'background.default' : 'grey.50'
          }}
        >
          <Box
            component="textarea"
            rows={1}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={aoTeclar}
            placeholder="Escreva uma mensagem"
            sx={{
              width: '100%',
              border: 'none',
              outline: 'none',
              resize: 'none',
              bgcolor: 'transparent',
              color: theme.palette.text.primary,
              fontFamily: theme.typography.fontFamily,
              fontSize: '0.8125rem',
              lineHeight: 1.5,
              maxHeight: 72,
              '&::placeholder': { color: theme.palette.text.secondary }
            }}
          />
        </Paper>
        <IconButton
          color="primary"
          variant="contained"
          disabled={!texto.trim()}
          onClick={enviar}
          aria-label="enviar mensagem"
          sx={{ borderRadius: '50%' }}
        >
          <SendOutlined />
        </IconButton>
      </Stack>
    </Stack>
  );
};

Conversa.propTypes = {
  membro: PropTypes.object,
  mensagens: PropTypes.array,
  digitando: PropTypes.bool,
  onEnviar: PropTypes.func,
  onVoltar: PropTypes.func
};

export default Conversa;
